import React, { useEffect, useMemo, useRef } from "react";
import { intlShape } from "@ai360/core/intl";
import { useIntl } from "react-intl";
import { FieldAPI, ReportAPI } from "@ai360/core";
import PropTypes from "prop-types";

import { ReportOptionsComponentProps } from "~/reports/reports/index";
import { getParsedCropData, getReportDataByReportType } from "~/reports/data/selectors";
import { useCropList } from "~/admin/setup/customer/use-crop-list";
import {
    IS_SELECTABLE,
    useLoggedInUserProductScopeOrgLevelGuid,
} from "~/admin/setup/customer/use-product-list";
import { useAppSelector } from "~/store/hooks";
import { model } from "~/reports/data";
import { Checkbox, DateInput, Section, SelectInput, SubSection } from "~/core";
import { messages } from "~/reports/i18n-messages";
import { messages as globalMessages } from "~/i18n-messages";
import { ISelectOption } from "~/core/components/select-input/model";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { picklistNames, selectors as picklistSelectors } from "~/core/picklist";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";

const plantingSeasonOptions: ISelectOption<string>[] = [
    { value: "Spring", label: "Spring" },
    { value: "Winter", label: "Winter" },
];

interface IMyagdataAcreageProps {
    selectedCrop: FieldAPI.ICrop | null;
}

function MyagdataAcreage({
    onInputChange,
    selectedCrop,
}: ReportOptionsComponentProps & IMyagdataAcreageProps) {
    const { formatMessage } = useIntl();
    const reportData = useAppSelector(getReportDataByReportType(MyagdataAcreage.reportName));
    const cropScopeOrgLevelGuid = useLoggedInUserProductScopeOrgLevelGuid();
    const { crops: rawCrops } = useCropList({ orgLevelGuid: cropScopeOrgLevelGuid });
    const crops = useMemo(
        () =>
            getParsedCropData(
                rawCrops.map((crop) => ({
                    cropGuid: crop.cropGuid,
                    cropId: String(crop.cropId),
                    cropName: crop.name,
                    activeYn: crop.activeYn,
                    isAgvanceLinked: crop.isAgvanceLinked,
                    ...(crop.isAgvanceLinked === false && {
                        getToolTip: () => formatMessage(globalMessages.cropNotLinkedInAgvance),
                    }),
                }))
            ),
        [rawCrops]
    );
    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const seasons = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        )
    );

    const selectedEventGuidList = useAppSelector(getSelectedEventGuidSet);
    const seasonsToDisplay = useRef<string[]>();

    useEffect(() => {
        onInputChange(model.PROPS_SELECTED_EVENT_GUID_LIST, [...selectedEventGuidList]);

        if (selectedEventGuidList.size > 0) {
            const seasonGuids = [];

            for (const eventGeneralGuid of selectedEventGuidList) {
                const event = agEventSummaryMap.get(eventGeneralGuid);
                if (event.agEventTypeName === "Planting") {
                    seasonGuids.push(event.croppingSeasonGuid);
                }
            }
            const seasonsText = [];
            for (const seasonGuid of seasonGuids) {
                const selectedSeason = seasons.find((season) => season.value === seasonGuid);
                if (seasonsText.indexOf(selectedSeason.label) === -1) {
                    seasonsText.push(selectedSeason.label);
                }
            }
            seasonGuids.sort();
            seasonsToDisplay.current = seasonsText;
        } else {
            seasonsToDisplay.current = [];
        }
    }, [selectedEventGuidList.size]);

    const optionsDisabled = selectedEventGuidList.size > 0;
    const croppingSeasonDisabled =
        reportData[model.PROPS_START_DATE] ||
        reportData[model.PROPS_END_DATE] ||
        selectedEventGuidList.size > 0;
    return (
        <Section className="report-options" testId="myagdata-acreage-options-section">
            <SubSection testId="myagdata-acreage-season-and-crop-subsection">
                <CroppingSeason
                    disabled={croppingSeasonDisabled}
                    required={!croppingSeasonDisabled}
                    reportType={MyagdataAcreage.reportName}
                    valuesToDisplay={seasonsToDisplay.current}
                />
                <SelectInput<FieldAPI.ICrop>
                    disabled={optionsDisabled}
                    optionIsHiddenKey={IS_SELECTABLE}
                    placeholderText={formatMessage(messages.crop)}
                    options={optionsDisabled ? [] : crops}
                    onChange={(value) => {
                        onInputChange(model.PROPS_CROP_GUID, value ? value.cropGuid : null);
                    }}
                    value={optionsDisabled ? null : (selectedCrop as FieldAPI.ICrop)}
                    testId="myagdata-acreage-crop-select"
                />
            </SubSection>
            <SubSection testId="myagdata-acreage-dates-subsection">
                <DateInput
                    disabled={optionsDisabled}
                    placeholderText={formatMessage(messages.startDate)}
                    onChange={(value) =>
                        onInputChange(model.PROPS_START_DATE, value ? value : null)
                    }
                    timeFormat={false}
                    value={optionsDisabled ? null : reportData[model.PROPS_START_DATE]}
                    testId="myagdata-acreage-start-date-date"
                />
                <DateInput
                    disabled={optionsDisabled}
                    placeholderText={formatMessage(messages.endDate)}
                    onChange={(value) => onInputChange(model.PROPS_END_DATE, value ? value : null)}
                    timeFormat={false}
                    value={optionsDisabled ? null : reportData[model.PROPS_END_DATE]}
                    testId="myagdata-acreage-end-date-date"
                />
            </SubSection>
            <SubSection
                className="two-report-options"
                testId="myagdata-acreage-planting-season-subsection"
            >
                <SelectInput
                    containerClassNames={["display-options-list"]}
                    required={true}
                    disabled={false}
                    clearable={false}
                    placeholderText={formatMessage(messages.plantingSeason)}
                    options={plantingSeasonOptions}
                    onChange={(value) => onInputChange(model.PROPS_PLANTING_SEASON, value)}
                    value={reportData[model.PROPS_PLANTING_SEASON]}
                    testId="myagdata-acreage-planting-season-select"
                />
                <Checkbox
                    disabled={false}
                    label={formatMessage(messages.sendGrowerApproval)}
                    onChange={(e, value) => onInputChange(model.PROPS_SEND_GROWER_APPROVAL, value)}
                    value={reportData[model.PROPS_SEND_GROWER_APPROVAL]}
                    testId="myagdata-acreage-send-grower-approval-checkbox"
                />
            </SubSection>
        </Section>
    );
}

MyagdataAcreage.propTypes = {
    intl: intlShape,
    reportPreferences: PropTypes.object,
    selectedCrop: PropTypes.object,
};

MyagdataAcreage.reportName = ReportAPI.ReportNames.MYAGDATA_ACREAGE;

MyagdataAcreage.reportPreferences = [model.PROPS_PLANTING_SEASON];

MyagdataAcreage.defaultReportOptions = {
    [model.PROPS_SEND_GROWER_APPROVAL]: false,
    [model.PROPS_PLANTING_SEASON]: "Spring",
    [model.PROPS_CROPPING_SEASON_GUID]: null,
};

export default MyagdataAcreage as ReportOptionsComponent<ReportOptionsComponentProps>;
